<script lang="ts">
  import type { S3FileEntry } from '../../services/s3-client';
  import { formatSize } from '../../services/s3-client';

  interface Props {
    files: S3FileEntry[];
    selectedFile: string | null;
    onselect: (key: string) => void;
    onexpand: (dir: string) => void;
    subFiles?: Record<string, S3FileEntry[]>;
  }

  let { files, selectedFile, onselect, onexpand, subFiles = {} }: Props = $props();

  let expandedDirs = $state<Set<string>>(new Set());

  const LARGE_FILE_THRESHOLD = 4000000; // 4MB
  const MD_FILES = new Set(['kw_generation.json', 'kw_validation.json', 'summary_generator.json']);

  function hasMdContent(fileName: string): boolean {
    return MD_FILES.has(fileName);
  }

  // Derive tree structure from flat file list
  const fileTree = $derived.by(() => {
    const rootFiles: S3FileEntry[] = [];
    const dirs = new Map<string, S3FileEntry[]>();
    for (const f of files) {
      if (f.type === 'directory') {
        // Keep existing directory entries
        rootFiles.push(f);
        continue;
      }
      const parts = f.key.split('/');
      if (parts.length > 1 && parts[0]) {
        const dir = parts[0];
        if (!dirs.has(dir)) dirs.set(dir, []);
        dirs.get(dir)!.push({ ...f, name: parts.slice(1).join('/') });
      } else {
        rootFiles.push(f);
      }
    }
    return { rootFiles, dirs };
  });

  function toggleDir(dir: string) {
    const next = new Set(expandedDirs);
    if (next.has(dir)) {
      next.delete(dir);
    } else {
      next.add(dir);
      if (!subFiles[dir]) onexpand(dir);
    }
    expandedDirs = next;
  }

  function toggleDerivedDir(dir: string) {
    const next = new Set(expandedDirs);
    if (next.has(dir)) {
      next.delete(dir);
    } else {
      next.add(dir);
    }
    expandedDirs = next;
  }

  function isLargeFile(entry: S3FileEntry): boolean {
    return entry.type === 'file' && entry.size >= LARGE_FILE_THRESHOLD;
  }
</script>

<div class="text-xs font-mono">
  <!-- Root files first -->
  {#each fileTree.rootFiles as entry (entry.key)}
    {#if entry.type === 'directory'}
      <!-- Directory from API -->
      <button
        onclick={() => toggleDir(entry.key)}
        class="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover transition-colors rounded"
      >
        <svg class="w-3 h-3 text-text-muted transition-transform {expandedDirs.has(entry.key) ? 'rotate-90' : ''}" fill="currentColor" viewBox="0 0 20 20">
          <path d="M6 4l8 6-8 6V4z" />
        </svg>
        <svg class="w-3.5 h-3.5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        <span class="text-text-primary">{entry.name.replace('/', '')}</span>
      </button>

      {#if expandedDirs.has(entry.key)}
        <div class="ml-5 border-l border-border-light pl-1">
          {#if subFiles[entry.key]}
            {#each subFiles[entry.key] as child (child.key)}
              <button
                onclick={() => onselect(`${entry.key}${child.key}`)}
                class="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover transition-colors rounded
                  {selectedFile === `${entry.key}${child.key}` ? 'bg-accent/10 text-accent' : ''}"
              >
                <svg class="w-3.5 h-3.5 text-text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span class="truncate">{child.name}</span>
                {#if hasMdContent(child.name)}
                  <span class="text-[8px] px-1 py-0.5 rounded bg-accent/20 text-accent font-semibold shrink-0">MD</span>
                {/if}
                <span class="ml-auto text-text-muted shrink-0">{formatSize(child.size)}</span>
              </button>
            {/each}
          {:else}
            <div class="px-3 py-1.5 text-text-muted animate-skeleton-pulse">Loading…</div>
          {/if}
        </div>
      {/if}
    {:else}
      <!-- Root file -->
      <button
        onclick={() => {
          if (isLargeFile(entry) && !confirm(`${entry.name} is ${formatSize(entry.size)}. Load anyway?`)) return;
          onselect(entry.key);
        }}
        class="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover transition-colors rounded
          {selectedFile === entry.key ? 'bg-accent/10 text-accent' : ''}"
      >
        <span class="w-3 h-3 shrink-0"></span>
        {#if isLargeFile(entry)}
          <svg class="w-3.5 h-3.5 text-warning shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        {:else}
          <svg class="w-3.5 h-3.5 text-text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        {/if}
        <span class="truncate">{entry.name}</span>
        <span class="ml-auto flex items-center gap-1.5 shrink-0">
          {#if hasMdContent(entry.name)}
            <span class="text-[8px] px-1 py-0.5 rounded bg-accent/20 text-accent font-semibold">MD</span>
          {/if}
          {#if isLargeFile(entry)}
            <span class="text-[9px] px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/30 font-sans">Large</span>
          {/if}
          <span class="text-text-muted">{formatSize(entry.size)}</span>
        </span>
      </button>
    {/if}
  {/each}

  <!-- Derived subdirectories from flat file paths -->
  {#each [...fileTree.dirs.entries()] as [dirName, dirFiles] (dirName)}
    <button
      onclick={() => toggleDerivedDir(dirName)}
      class="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover transition-colors rounded"
    >
      <svg class="w-3 h-3 text-text-muted transition-transform {expandedDirs.has(dirName) ? 'rotate-90' : ''}" fill="currentColor" viewBox="0 0 20 20">
        <path d="M6 4l8 6-8 6V4z" />
      </svg>
      <svg class="w-3.5 h-3.5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
      <span class="text-text-primary">{dirName}</span>
      <span class="ml-auto text-text-muted shrink-0">{dirFiles.length}</span>
    </button>

    {#if expandedDirs.has(dirName)}
      <div class="ml-5 border-l border-border-light pl-1">
        {#each dirFiles as child (child.key)}
          <button
            onclick={() => {
              if (isLargeFile(child) && !confirm(`${child.name} is ${formatSize(child.size)}. Load anyway?`)) return;
              onselect(child.key);
            }}
            class="w-full text-left flex items-center gap-2 px-3 py-1.5 hover:bg-bg-hover transition-colors rounded
              {selectedFile === child.key ? 'bg-accent/10 text-accent' : ''}"
          >
            <svg class="w-3.5 h-3.5 text-text-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span class="truncate">{child.name}</span>
            <span class="ml-auto flex items-center gap-1.5 shrink-0">
              {#if hasMdContent(child.name)}
                <span class="text-[8px] px-1 py-0.5 rounded bg-accent/20 text-accent font-semibold">MD</span>
              {/if}
              {#if isLargeFile(child)}
                <span class="text-[9px] px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/30 font-sans">Large</span>
              {/if}
              <span class="text-text-muted">{formatSize(child.size)}</span>
            </span>
          </button>
        {/each}
      </div>
    {/if}
  {/each}
</div>
